"use client";
import { CardArticle as UICardArticle } from "@repo/ui-parkmobile/CardArticle";
import type { CardArticle, Article } from "@/generated/sb";
import {
  linkResolver,
  titleResolver,
  textResolver,
  articleResolver,
  imageStoryblokResolver,
} from "@/lib/storyblok/adaptors";
import { storyblokEditable } from "@storyblok/react/rsc";
import { useMemo } from "react";
import { useGlobalTranslations } from "@/hooks/useGlobalTranslations";
import { trackArticleClick } from "@/utils/trackingScripts";
import { ISbStoryData } from "storyblok-js-client";
import { deleteAutoSlug } from "@/utils/autoSlug";

export interface CardArticleProps {
  blok: CardArticle;
  section?: string;
}

export default function CardArticle({ blok, section }: CardArticleProps) {
  const resolvedArticle =
    blok.article && typeof blok.article === "object" ? blok.article : null;
  const translations = useGlobalTranslations(["readMore"]);

  const articleData = useMemo(() => {
    if (!resolvedArticle) return null;

    const articleData = articleResolver({
      article: resolvedArticle as ISbStoryData<Article>,
      imageAspectRatio: "1/1",
      disableDescription: blok.disableDescription,
    });

    if (!articleData) return null;

    return {
      image: articleData.image,
      title: {
        content: articleData.title,
        level: "h6" as const,
        fontSize: "p" as const,
        className: blok.disableDescription ? "uipm:font-semibold" : undefined,
      },
      text: articleData.description,
      date: resolvedArticle.created_at,
      link: {
        href: `/${deleteAutoSlug(resolvedArticle.full_slug)}`,
        text: translations.readMore || "Read More",
      },
    };
  }, [resolvedArticle, translations, blok.disableDescription]);

  if (!blok) {
    return null;
  }

  const imageBlok = blok.image?.[0];
  const imageAsset = imageStoryblokResolver(imageBlok);
  const linkBlok = blok.link?.[0];
  const linkProps = linkBlok ? linkResolver([linkBlok]) : null;

  const handleCardClick = (title?: string, date?: string, ctaText?: string) => {
    const linkHref = articleData?.link?.href || (linkProps && linkBlok ? String(linkProps.href || "") : "");
    trackArticleClick({
      section: section || "Reservation – Case Studies",
      config: "cardArticle",
      articleTitle: title || "",
      articleDate: date || "",
      ctaText: ctaText || "",
      link: linkHref,
    });
  };

  return (
    <div {...storyblokEditable(blok)}>
      <UICardArticle
        layout={blok.layout || "vertical"}
        backgroundColor={blok.backgroundColor || "primary"}
        image={articleData?.image || imageAsset}
        title={articleData?.title || titleResolver(blok.title) || undefined}
        text={articleData?.text || textResolver(blok.text) || undefined}
        link={
          articleData?.link ||
          (linkProps && linkBlok
            ? {
                text: linkBlok.text || "",
                href: String(linkProps.href || ""),
                target: linkProps.target,
              }
            : undefined)
        }
        date={articleData?.date || blok.date}
        onCardClick={handleCardClick}
      />
    </div>
  );
}
